// ChatInput.jsx
import React, { useEffect, useRef, useState } from 'react'
import styles from './message-input.module.css'
import cn from '@/utils/cn.ts'
import ArrowRight from '@/assets/icons/arrowRightLine.svg?react'
import Button from '@/shared/Buttons/Button'

interface IProps {
  onSend: (text: string) => void
  placeholder?: string
  maxRows?: number
  disabled?: boolean
  loading?: boolean
}

const MessageInput = ({
  onSend,
  placeholder = 'Message ChatGPT…',
  disabled = false,
  maxRows = 10,
  loading,
}: IProps) => {
  const [value, setValue] = useState('')
  const textareaRef = useRef(null)

  // Auto-grow textarea height as the user types
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return

    // Reset height to recalc correctly when deleting text
    el.style.height = 'auto'

    const computed = window.getComputedStyle(el)
    const lineHeight = parseFloat(computed.lineHeight) || 20
    const maxHeight = lineHeight * maxRows

    const scrollHeight = el.scrollHeight
    const height = Math.min(scrollHeight, maxHeight)

    el.style.height = `${height}px`
    el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [value, maxRows])

  const sendMessage = () => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    if (onSend) onSend(trimmed)

    setValue('')
  }

  const handleKeyDown = (e) => {
    // Enter = send, Shift+Enter = new line
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.nativeEvent.isComposing // IME safety
    ) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className={styles.wrapper}>
      <textarea
        ref={textareaRef}
        className={cn(styles.textarea, 'text-lg')}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <Button
        size="large"
        type="primary"
        className={styles.sendButton}
        icon={<ArrowRight style={{ width: '24px', height: '24px', marginBottom: '2px' }} />}
        loading={loading}
        onClick={sendMessage}
      />
    </div>
  )
}

export default MessageInput
